import React, { useMemo } from 'react';
import { useDroppable, useDraggable } from '@dnd-kit/core';
import { GripVertical } from 'lucide-react';
import { getDisplayName } from '../../lib/utils';

const DraggableLeadCard = ({ lead }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `lead-${lead.id}`,
    data: { type: 'lead', leadId: lead.id },
  });

  const dragStyle = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, zIndex: 100 }
    : {};

  const serviceName = lead.service || null;
  const customerName = getDisplayName(lead, '');
  const city = lead.address?.city || lead.clientInfo?.city || '';

  return (
    <div
      ref={setNodeRef}
      style={{ ...dragStyle, opacity: isDragging ? 0.75 : 1 }}
      {...attributes}
      {...listeners}
      className="w-full bg-white rounded-md p-2 border border-gray-200 hover:border-gray-300 shadow-[0_1px_2px_rgba(0,0,0,0.04)] hover:shadow-md transition-all cursor-grab active:cursor-grabbing flex items-start gap-2"
      data-testid={`staging-lead-${lead.id}`}
    >
      {/* Content hierarchy */}
      <div className="flex-1 overflow-hidden min-w-0">
        {/* Line 1: service name (bold) */}
        <div className="font-semibold text-[11px] text-gray-900 truncate leading-snug">
          {serviceName || customerName || 'Unnamed lead'}
        </div>
        {/* Line 2: customer name — only when service is shown on line 1 */}
        {serviceName && customerName && (
          <div className="text-[10px] text-gray-600 truncate leading-snug mt-0.5">
            {customerName}
          </div>
        )}
        {/* Line 3: city (optional) */}
        {city && (
          <div className="text-[10px] text-gray-400 truncate leading-snug mt-0.5">
            {city}
          </div>
        )}
      </div>
      {/* Drag affordance */}
      <div className="flex flex-col items-end gap-1 flex-shrink-0">
        {lead.paymentConfirmed && (
          <span
            className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200 leading-none whitespace-nowrap"
            data-testid={`paid-badge-${lead.id}`}
            title="Payment received"
          >
            Paid
          </span>
        )}
        <GripVertical className="h-3.5 w-3.5 text-gray-300 mt-0.5" />
      </div>
    </div>
  );
};

const UnscheduledStagingColumn = ({ leads, tasks }) => {
  const unscheduledLeads = useMemo(() => {
    if (!leads || !tasks) return [];

    // FINAL CONTRACT: only Approved leads (quote accepted, not yet scheduled) are shown here.
    const approvedLeads = leads.filter((lead) => lead.stage === 'Approved');
    if (approvedLeads.length === 0) return [];

    const leadHasScheduledJob = new Set();

    tasks.forEach((task) => {
      if (task.type !== 'job') return;
      if (!task.leadId) return;
      const rawStart = task.scheduledStart;
      if (!rawStart) return; // only treat as scheduled if scheduledStart is set
      const dt = new Date(rawStart);
      if (Number.isNaN(dt.getTime())) return;
      leadHasScheduledJob.add(task.leadId);
    });

    return approvedLeads.filter((lead) => !leadHasScheduledJob.has(lead.id));
  }, [leads, tasks]);

  const { setNodeRef, isOver } = useDroppable({
    id: 'staging-column',
    data: { type: 'staging' },
  });

  return (
    <div
      ref={setNodeRef}
      className={`w-56 flex-shrink-0 bg-white border border-gray-200 rounded-md flex flex-col h-full transition-colors ${
        isOver ? 'bg-[rgba(242,107,29,0.08)] border-[var(--cb-orange)]' : ''
      }`}
    >
      {/* Header - matches calendar grid header height */}
      <div className="h-10 border-b border-gray-200 bg-gray-50 flex items-center justify-between px-3">
        <h3 className="text-xs font-semibold text-gray-700">Unscheduled Jobs</h3>
        {unscheduledLeads.length > 0 && (
          <span
            className="bg-[var(--cb-orange)] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center leading-none"
            data-testid="unscheduled-count-badge"
          >
            {unscheduledLeads.length}
          </span>
        )}
      </div>

      {/* Scrollable content area - full height lane */}
      <div className="flex-1 overflow-y-auto p-2 space-y-2">
        {isOver && (
          <div className="border-2 border-dashed border-[var(--cb-orange)] rounded-md p-3 text-center text-[11px] text-[var(--cb-orange)] bg-[rgba(242,107,29,0.05)]">
            Drop here to unschedule
          </div>
        )}
        
        {unscheduledLeads.length === 0 && !isOver ? (
          <p className="text-[11px] text-gray-400 text-center py-4">
            No unscheduled jobs
          </p>
        ) : (
          unscheduledLeads.map((lead) => <DraggableLeadCard key={lead.id} lead={lead} />)
        )}
      </div>

      {/* Footer hint */}
      <div className="border-t border-gray-100 px-2 py-1.5 bg-gray-50">
        <p className="text-[10px] text-gray-400 text-center">
          Drag jobs here to unschedule
        </p>
      </div>
    </div>
  );
};

export default UnscheduledStagingColumn;
